import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import shortid from 'shortid'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

import Icon from 'components/elements/Icon'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './globalOverlayStack.module.scss'
import FloatingToolbar, { Option } from 'components/elements/FloatingToolbar'

type Props = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  mobileToolbarOptions?: Option[]
  rootClassName?: string
  backButtonText?: string
  remainMounted?: boolean
  id?: string
  noWeirdClose?: boolean
}

const TRANSITION_TIME = 400 // Should match the transition time in the CSS

const GlobalOverlayStack = React.forwardRef(
  (
    {
      open,
      onClose,
      children,
      className = '',
      mobileToolbarOptions = [],
      rootClassName = '',
      remainMounted,
      noWeirdClose = false,
      id: parentId,
    }: Props,
    parentRef: any,
  ) => {
    if (typeof document === 'undefined') return null

    const id = useRef(parentId || shortid.generate())

    const openRef = useRef(open)
    const componentEnabledNoScrollWhileOpeneing = useRef(false)

    const [ready, setReady] = useState(false) // use this rather than open. Open is used from the parent, but we need to append the ID to the stackOverlayIds before we can show the content
    const [visable, setVisable] = useState(false)
    const [contentVisable, setContentVisable] = useState(false) // used to transition the content, not the background
    const [closeClickStarted, setCloseClickStarted] = useState(false)

    const [isActiveStackElement, setIsActiveStackElement] = useState(false)

    const stackOverlayIds = useAppSelector(state => state.active.stackOverlayIds)
    const hideStackOverlay = useAppSelector(state => state.active.hideStackOverlay)

    const [setActiveState] = useActionless(SET_ACTIVE_STATE)

    // Keylistener setup && destruction
    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown)

      return () => {
        document.removeEventListener('keydown', handleKeyDown)

        // This is all part of a failsafe cleanup process.
        // This way if the overlay is removed from the DOM without having been closed, it will still be removed from the stack
        if (!openRef.current) return

        openRef.current = false
        setActiveState({ stackOverlayIds: stackOverlayIds.filter(overlayId => overlayId !== id.current) })
      }
    }, [])

    useEffect(() => {
      const scrollingIsActive = !document.body.classList.contains('no-scrolling')

      if (!open && !visable) return

      openRef.current = open

      if (open) {
        if (scrollingIsActive) {
          componentEnabledNoScrollWhileOpeneing.current = true
          document.body.classList.add('no-scrolling')
        }

        setActiveState({ stackOverlayIds: [...stackOverlayIds, id.current] })
        setReady(true)

        setTimeout(() => {
          setVisable(true)
          setContentVisable(true)
        }, TRANSITION_TIME)
      } else {
        if (componentEnabledNoScrollWhileOpeneing.current) {
          componentEnabledNoScrollWhileOpeneing.current = false
          document.body.classList.remove('no-scrolling')
        }

        setVisable(false)
        setContentVisable(false)

        setTimeout(() => {
          setActiveState({ stackOverlayIds: stackOverlayIds.filter(overlayId => overlayId !== id.current) })
          setReady(false)
        }, TRANSITION_TIME)
      }

      // If the overlay is removed from the DOM prior and it enabled no-scrolling - remove no-scrolling
      return () => {
        if (componentEnabledNoScrollWhileOpeneing.current) {
          document.body.classList.remove('no-scrolling')
        }
      }
    }, [open])

    useEffect(() => {
      if (!open && !visable) return

      const isActiveStackElement = stackOverlayIds[stackOverlayIds.length - 1] === id.current

      if (isActiveStackElement) {
        setIsActiveStackElement(isActiveStackElement)
        setTimeout(() => setContentVisable(true), TRANSITION_TIME / 2)
      } else {
        setContentVisable(false)
        setTimeout(() => setIsActiveStackElement(isActiveStackElement), TRANSITION_TIME / 2)
      }
    }, [stackOverlayIds])

    useEffect(() => {
      // Regardless of the components state when this component is unmounted
      // Check the store, and remove the ID from the stack if it still exists
      // This is a failsafe to be sure the ID is removed from the stack even if the element is removed via unorthodox means
      return () => {
        const currentIds = store.getState().active.stackOverlayIds
        const stillInStack = store.getState().active.stackOverlayIds.includes(id.current)

        // If after we
        if (stillInStack) {
          setActiveState({ stackOverlayIds: currentIds.filter(overlayId => overlayId !== id.current) })
        }
      }
    }, [])

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!onClose) return
      if (openRef.current === false) return

      if (basicKeybindSkips(e)) return

      if (e.key === 'Escape') {
        e.preventDefault() // prevents exiting fullscreen on macos (I think)

        onClose()
      }
    }

    // Using the built in onClick means that if you start the click in the modal and end it in the dimmer area it closes
    // the modal. This is frustrating when selecting text in an input field. To avoid this we track if the click started
    // in the dimmer area and only close the modal if it did and it also ended in the dimmer area.
    const handleWeirdCloseMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (noWeirdClose) return
      if (e.clientX >= e.currentTarget.clientWidth) return // Skip if they clicked on a scrollbar
      if (e.target === e.currentTarget) setCloseClickStarted(true)
      else setCloseClickStarted(false)
    }
    const handleWeirdCloseMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
      if (noWeirdClose) return
      if (e.target === e.currentTarget && closeClickStarted) onClose && onClose()
      setCloseClickStarted(false)
    }

    if (!ready && !visable && !remainMounted) return null

    const isRootComponent = stackOverlayIds[0] === id.current

    return ReactDOM.createPortal(
      <div
        id={id.current}
        className={`
          ${styles.overlay}
          ${isRootComponent ? styles.rootOverlay : ''}
          ${visable && !hideStackOverlay ? styles.visable : ''}
          ${rootClassName}
        `}>
        <div
          ref={parentRef}
          onMouseDown={handleWeirdCloseMouseDown}
          onMouseUp={handleWeirdCloseMouseUp}
          className={`
          ${styles.content}
          ${contentVisable && !hideStackOverlay ? styles.visable : ''}
          ${!isActiveStackElement ? styles.inactive : ''}
        `}>
          <div className={`${styles.primaryContent} ${className}`}>{children}</div>
        </div>

        <div
          className={`${styles.desktopNavigation} ${contentVisable ? styles.visable : ''} ${!isActiveStackElement ? styles.inactive : ''}`}>
          <button onClick={onClose}>
            <Icon name={isRootComponent ? 'close' : 'arrow left'} />
            <span>{isRootComponent ? 'Close' : 'Back'}</span>
          </button>
        </div>

        <FloatingToolbar
          hideOnScrollRef={parentRef}
          primaryOptions={[
            {
              icon: isRootComponent ? 'close' : 'arrow left',
              label: isRootComponent ? 'Close' : 'Back',
              onClick: onClose,
            },
            ...mobileToolbarOptions,
          ]}
          className={styles.mobileToolbar}
        />

        <div className={styles.grid} />
      </div>,
      document.body,
    )
  },
)

export default GlobalOverlayStack
